/**
 * LivingFloor Component - Interactive 3D floor that responds to panels and user activity
 * Creates a procedural, living floor surface with particle systems and aurora lighting effects
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { useParticleSystem, type ParticleSystemConfig } from './use-particle-system';
import { createFloorMaterial, createCursorTrailMaterial, FLOOR_PALETTE } from './living-floor-materials';
import { useAudioReactivity } from './use-audio-reactivity';

// Enable debug logging by setting localStorage.setItem('LIVING_FLOOR_DEBUG', 'true')
const DEBUG =
  typeof window !== 'undefined' && localStorage.getItem('LIVING_FLOOR_DEBUG') === 'true';

export interface LivingFloorProps {
  panelPositions?: Array<{ x: number; y: number; z: number }>;
  cursorPos?: THREE.Vector3;
  activeUsers?: Array<{ x: number; y: number; z: number; intensity: number }>;
  enabled?: boolean;
  particleConfig?: ParticleSystemConfig;
  width?: number;
  height?: number;
  onReady?: (floor: LivingFloorInstance) => void;
}

export interface LivingFloorInstance {
  updatePanels: (positions: Array<{ x: number; y: number; z: number }>) => void;
  updateCursor: (pos: THREE.Vector3) => void;
  updateActiveUsers: (users: Array<{ x: number; y: number; z: number; intensity: number }>) => void;
  dispose: () => void;
}

const DEFAULT_PARTICLE_CONFIG: ParticleSystemConfig = {
  count: 30000,
  speed: 0.05,
  repulsion: 0.8,
  attraction: 0.03,
  friction: 0.92,
  boundsX: 100,
  boundsZ: 100,
  particleSize: 2.0,
};

// CSS aurora gradient — rendered as container background behind the transparent Three.js canvas.
// Avoids GLSL ShaderMaterial prism artifacts; soft radial gradients are handled natively by the browser.
const AURORA_CSS_BACKGROUND = [
  'radial-gradient(ellipse 90% 50% at 18% 62%, rgba(85,0,255,0.52) 0%, transparent 68%)',
  'radial-gradient(ellipse 85% 48% at 82% 58%, rgba(0,212,242,0.46) 0%, transparent 65%)',
  'radial-gradient(ellipse 65% 38% at 50% 82%, rgba(190,0,255,0.36) 0%, transparent 60%)',
  'radial-gradient(ellipse 55% 30% at 30% 40%, rgba(40,0,180,0.28) 0%, transparent 70%)',
  FLOOR_PALETTE.floorBase,
].join(', ');

/**
 * Living Floor - Procedural 3D floor with reactive particle system and bioluminescent aurora
 * Renders as the background floor surface, responds to panel positions and cursor
 */
export const LivingFloor = React.forwardRef<LivingFloorInstance, LivingFloorProps>(
  (
    {
      panelPositions = [],
      cursorPos = new THREE.Vector3(0, 0, 0),
      activeUsers = [],
      enabled = true,
      particleConfig = DEFAULT_PARTICLE_CONFIG,
      width = 1200,
      height = 800,
      onReady,
    },
    _ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const floorMeshRef = useRef<THREE.Mesh | null>(null);
    const particlesRef = useRef<THREE.Points | null>(null);
    const auroraDriftLightsRef = useRef<THREE.PointLight[]>([]);
    const cursorTrailRef = useRef<THREE.Points | null>(null);
    const cursorTrailPositionsRef = useRef<Float32Array | null>(null);
    const cursorTrailOpacitiesRef = useRef<Float32Array | null>(null);
    const cursorTrailIndexRef = useRef(0);
    const floorOriginalYRef = useRef<Float32Array | null>(null);

    // State for particle system
    const particleSystem = useParticleSystem(particleConfig);
    const audioReactivity = useAudioReactivity();
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Track data for updates
    const dataRef = useRef({
      panelPositions: [...panelPositions],
      cursorPos: cursorPos.clone(),
      activeUsers: [...activeUsers],
    });

    // Update data refs when props change
    useEffect(() => {
      dataRef.current = {
        panelPositions: [...panelPositions],
        cursorPos: cursorPos.clone(),
        activeUsers: [...activeUsers],
      };
    }, [panelPositions, cursorPos, activeUsers]);

    // Initialize Three.js scene (only once, or when enabled changes)
    useEffect(() => {
      if (!containerRef.current || !enabled) {
        return;
      }

      try {
        if (DEBUG) {
          console.log('[🌊 LivingFloor] Initializing with dimensions:', {
            width,
            height,
            clientWidth: containerRef.current.clientWidth,
            clientHeight: containerRef.current.clientHeight,
          });
        }

        // Use actual container dimensions if available, otherwise use props
        const actualWidth = containerRef.current.clientWidth || width;
        const actualHeight = containerRef.current.clientHeight || height;

        // Create scene — transparent so CSS aurora gradient shows through
        const scene = new THREE.Scene();
        scene.background = null;
        sceneRef.current = scene;

        // Create camera
        const camera = new THREE.PerspectiveCamera(75, actualWidth / actualHeight, 0.1, 1000);
        camera.position.set(0, 30, 40);
        camera.lookAt(0, 0, 0);
        cameraRef.current = camera;

        // Create renderer
        const renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          precision: 'highp',
        });
        renderer.setSize(actualWidth, actualHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // Style canvas to fill container properly
        renderer.domElement.style.display = 'block';
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '0';
        renderer.domElement.style.left = '0';
        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = '100%';

        rendererRef.current = renderer;

        if (DEBUG) {
          console.log('[🌊 LivingFloor] Appending canvas to container');
        }
        containerRef.current.appendChild(renderer.domElement);

        // Setup lighting — aurora-tinted illumination
        setupLighting(scene);

        // Aurora drift point lights — close to floor, decay=1 so color reaches the surface
        const driftLights: THREE.PointLight[] = [
          (() => {
            const l = new THREE.PointLight(0x5500ff, 4, 55, 1); // Violet
            l.position.set(-20, 3, -15);
            scene.add(l);
            return l;
          })(),
          (() => {
            const l = new THREE.PointLight(0x00d4f2, 3.5, 55, 1); // Cyan
            l.position.set(22, 3, -18);
            scene.add(l);
            return l;
          })(),
          (() => {
            const l = new THREE.PointLight(0xcc00ff, 2.5, 45, 1); // Magenta
            l.position.set(-5, 3, -30);
            scene.add(l);
            return l;
          })(),
        ];
        auroraDriftLightsRef.current = driftLights;

        // Create floor geometry with displacement
        const floorGeometry = createFloorGeometry();
        const floorMaterial = createFloorMaterial();

        const floorMesh = new THREE.Mesh(floorGeometry, floorMaterial);
        floorMesh.rotation.x = -Math.PI / 2;
        floorMesh.receiveShadow = true;
        scene.add(floorMesh);
        floorMeshRef.current = floorMesh;

        // Store original Y positions for wave animation
        const floorPositions = floorGeometry.attributes.position.array as Float32Array;
        const originalY = new Float32Array(floorPositions.length / 3);
        for (let i = 0; i < originalY.length; i++) {
          originalY[i] = floorPositions[i * 3 + 1]; // Y is index 1 (plane pre-rotation)
        }
        floorOriginalYRef.current = originalY;

        // Create particle system
        if (particleSystem.geometry && particleSystem.material) {
          const particles = new THREE.Points(particleSystem.geometry, particleSystem.material);
          particles.position.y = 5; // Position well above floor to avoid depth testing issues
          particles.renderOrder = 1; // Ensure particles render after floor
          scene.add(particles);
          particlesRef.current = particles;
        } else {
          console.warn('[LivingFloor] Particle system missing geometry or material');
        }

        // Create cursor trail
        const TRAIL_LENGTH = 24;
        const trailGeometry = new THREE.BufferGeometry();
        const trailPositions = new Float32Array(TRAIL_LENGTH * 3);
        const trailOpacities = new Float32Array(TRAIL_LENGTH);
        trailGeometry.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));
        const trailMaterial = createCursorTrailMaterial();
        trailMaterial.blending = THREE.AdditiveBlending;
        const trailPoints = new THREE.Points(trailGeometry, trailMaterial);
        trailPoints.position.y = 0.5;
        trailPoints.renderOrder = 2;
        scene.add(trailPoints);
        cursorTrailRef.current = trailPoints;
        cursorTrailPositionsRef.current = trailPositions;
        cursorTrailOpacitiesRef.current = trailOpacities;
        cursorTrailIndexRef.current = 0;

        setIsInitialized(true);

        // Notify parent that floor is ready
        if (onReady) {
          onReady({
            updatePanels: (positions) => {
              dataRef.current.panelPositions = [...positions];
            },
            updateCursor: (pos) => {
              dataRef.current.cursorPos = pos.clone();
            },
            updateActiveUsers: (users) => {
              dataRef.current.activeUsers = [...users];
            },
            dispose: () => {
              cleanup();
            },
          });
        }

        return () => {
          cleanup();
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        console.error('[Living Floor] Initialization error:', message);
        setError(message);
      }
    }, [enabled]); // Only depend on enabled, not width/height

    // Cleanup function
    const cleanup = useCallback(() => {
      if (DEBUG) {
        console.log('[🌊 LivingFloor] Cleanup called');
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (floorMeshRef.current) {
        floorMeshRef.current.geometry.dispose();
        if (floorMeshRef.current.material instanceof THREE.Material) {
          floorMeshRef.current.material.dispose();
        }
      }
      // Dispose aurora drift lights and remove from scene
      if (sceneRef.current) {
        for (const light of auroraDriftLightsRef.current) {
          sceneRef.current.remove(light);
        }
      }
      auroraDriftLightsRef.current = [];
      // Dispose cursor trail Points and remove from scene
      if (cursorTrailRef.current) {
        cursorTrailRef.current.geometry.dispose();
        if (cursorTrailRef.current.material instanceof THREE.Material) {
          cursorTrailRef.current.material.dispose();
        }
        sceneRef.current?.remove(cursorTrailRef.current);
        cursorTrailRef.current = null;
      }
      // Dispose particle system Points and remove from scene to prevent accumulation on re-init
      if (particlesRef.current) {
        particlesRef.current.geometry.dispose();
        if (particlesRef.current.material instanceof THREE.Material) {
          particlesRef.current.material.dispose();
        }
        sceneRef.current?.remove(particlesRef.current);
        particlesRef.current = null;
      }
      if (containerRef.current && rendererRef.current) {
        try {
          containerRef.current.removeChild(rendererRef.current.domElement);
        } catch {
          // Already removed
        }
      }
    }, []);

    // Render loop
    useEffect(() => {
      if (DEBUG) {
        console.log('[🌊 LivingFloor] Render loop check:', {
          isInitialized,
          hasRenderer: !!rendererRef.current,
          hasScene: !!sceneRef.current,
          hasCamera: !!cameraRef.current,
        });
      }

      if (!isInitialized || !rendererRef.current || !sceneRef.current || !cameraRef.current) {
        if (DEBUG) {
          console.warn('[🌊 LivingFloor] Cannot start render loop - missing:', {
            isInitialized,
            renderer: !!rendererRef.current,
            scene: !!sceneRef.current,
            camera: !!cameraRef.current,
          });
        }
        return;
      }

      if (DEBUG) {
        console.log(
          '[🌊 LivingFloor] ✅ Starting render loop with scene children:',
          sceneRef.current.children.length
        );
      }

      let animationId: number;
      let frameCount = 0;
      const clock = new THREE.Clock();

      const animate = () => {
        try {
          animationId = requestAnimationFrame(animate);
          const delta = clock.getDelta();
          const elapsedTime = clock.getElapsedTime();

          frameCount++;

          // Sample audio for reactivity
          const audio = audioReactivity.enabled ? audioReactivity.sample() : null;
          const audioAmp = audio?.amplitude ?? 0;

          // Update floor material (breathing effect)
          if (
            floorMeshRef.current &&
            floorMeshRef.current.material instanceof THREE.MeshStandardMaterial
          ) {
            const material = floorMeshRef.current.material;
            material.emissiveIntensity = 0.4 + 0.25 * Math.sin(elapsedTime * 0.4) + audioAmp * 0.3;
          }

          // Animated wave displacement on floor mesh (every 3rd frame to save CPU)
          if (floorMeshRef.current && floorOriginalYRef.current && frameCount % 3 === 0) {
            const geo = floorMeshRef.current.geometry as THREE.BufferGeometry;
            const positions = geo.attributes.position.array as Float32Array;
            const originalY = floorOriginalYRef.current;
            const userCount = dataRef.current.activeUsers.length;
            // Wave intensity scales with user activity and audio
            const waveIntensity = 0.15 + userCount * 0.05 + audioAmp * 0.4;
            const waveSpeed = 0.6 + audioAmp * 1.5;

            for (let i = 0; i < originalY.length; i++) {
              const x = positions[i * 3];
              const z = positions[i * 3 + 2];
              const wave =
                Math.sin(x * 0.12 + elapsedTime * waveSpeed) *
                Math.cos(z * 0.1 + elapsedTime * waveSpeed * 0.7) *
                waveIntensity +
                Math.sin(x * 0.05 - elapsedTime * 0.3 + z * 0.08) * waveIntensity * 0.4;
              positions[i * 3 + 1] = originalY[i] + wave;
            }
            geo.attributes.position.needsUpdate = true;
          }

          // Update cursor trail
          if (cursorTrailPositionsRef.current && cursorTrailOpacitiesRef.current) {
            const trailPos = cursorTrailPositionsRef.current;
            const trailOp = cursorTrailOpacitiesRef.current;
            const cursor = dataRef.current.cursorPos;
            const TRAIL_LENGTH = 24;

            // Only add trail point if cursor has moved from origin
            if (cursor.x !== 0 || cursor.z !== 0) {
              const idx = cursorTrailIndexRef.current % TRAIL_LENGTH;
              trailPos[idx * 3] = cursor.x;
              trailPos[idx * 3 + 1] = 0;
              trailPos[idx * 3 + 2] = cursor.z;
              trailOp[idx] = 1.0;
              cursorTrailIndexRef.current++;
            }

            // Fade all trail points
            for (let i = 0; i < TRAIL_LENGTH; i++) {
              trailOp[i] *= 0.92;
              // Sink fading points below floor
              if (trailOp[i] < 0.05) {
                trailPos[i * 3 + 1] = -100;
              }
            }

            if (cursorTrailRef.current) {
              const geo = cursorTrailRef.current.geometry;
              (geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
            }
          }

          // Animate aurora drift lights — orbital sweep across the floor
          const driftLights = auroraDriftLightsRef.current;
          if (driftLights.length >= 3) {
            // Violet — sweeps left third of floor
            driftLights[0].position.x = -20 + Math.sin(elapsedTime * 0.5) * 14;
            driftLights[0].position.z = -15 + Math.cos(elapsedTime * 0.35) * 10;
            driftLights[0].intensity = 3.5 + 1.2 * Math.sin(elapsedTime * 0.7);

            // Cyan — counter-sweeps right third
            driftLights[1].position.x = 22 + Math.sin(elapsedTime * 0.45 + Math.PI) * 14;
            driftLights[1].position.z = -18 + Math.cos(elapsedTime * 0.3 + 1.5) * 10;
            driftLights[1].intensity = 3.0 + 1.0 * Math.cos(elapsedTime * 0.65);

            // Magenta — slow wide orbit across the back
            driftLights[2].position.x = -5 + Math.sin(elapsedTime * 0.3 + 1.0) * 18;
            driftLights[2].position.z = -30 + Math.cos(elapsedTime * 0.25) * 12;
            driftLights[2].intensity = 2.0 + 0.8 * Math.sin(elapsedTime * 0.55 + 0.5);
          }

          // Update particles
          try {
            particleSystem.update({
              panelPositions: dataRef.current.panelPositions,
              cursorPos: dataRef.current.cursorPos,
              activeUsers: dataRef.current.activeUsers,
              time: elapsedTime,
              audioAmplitude: audioAmp,
            });
          } catch (updateError) {
            console.error('[🌊 LivingFloor] Particle update error:', updateError);
          }

          // Render scene
          if (frameCount === 1 && DEBUG) {
            console.log('[🌊 LivingFloor] ✅ First frame rendered', {
              sceneChildren: sceneRef.current!.children.length,
              cameraPos: cameraRef.current!.position,
              particleSystem: !!particleSystem,
              hasGeometry: !!particleSystem.geometry,
              hasMaterial: !!particleSystem.material,
            });
          }

          rendererRef.current!.render(sceneRef.current!, cameraRef.current!);
        } catch (error) {
          console.error('[🌊 LivingFloor] Render error:', error);
        }
      };

      if (DEBUG) {
        console.log('[🌊 LivingFloor] Calling animate()');
      }
      animate();

      return () => {
        cancelAnimationFrame(animationId);
      };
    }, [isInitialized, particleSystem, audioReactivity]);

    // Handle window resize (separate from initialization)
    useEffect(() => {
      const handleResize = () => {
        if (!cameraRef.current || !rendererRef.current || !containerRef.current) return;

        const newWidth = containerRef.current.clientWidth || width;
        const newHeight = containerRef.current.clientHeight || height;

        if (DEBUG) {
          console.log('[🌊 LivingFloor] Resizing to', { newWidth, newHeight });
        }
        cameraRef.current.aspect = newWidth / newHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(newWidth, newHeight);
      };

      // Initial resize on mount
      handleResize();

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []); // Empty deps - only run on mount

    if (error) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: FLOOR_PALETTE.floorBase,
            color: '#ff6b6b',
            fontSize: '14px',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div>🚨 Living Floor Error</div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>{error}</div>
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: AURORA_CSS_BACKGROUND,
        }}
      />
    );
  }
);

LivingFloor.displayName = 'LivingFloor';

/**
 * Create floor geometry with tesselation
 */
function createFloorGeometry(): THREE.BufferGeometry {
  const size = 100;
  const segments = 128; // High resolution for wave animation

  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);

  // Optional: Add initial displacement for interesting surface
  const positions = geometry.attributes.position.array as Float32Array;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const z = positions[i + 2];
    // Perlin-like noise (simplified)
    positions[i + 1] = Math.sin(x * 0.1) * Math.cos(z * 0.1) * 0.3;
  }

  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Setup scene lighting — aurora-tinted with deep void ambience
 */
function setupLighting(scene: THREE.Scene) {
  // Very low ambient — void should be dark, color comes from aurora planes and drift lights
  const ambientLight = new THREE.AmbientLight(0x080820, 0.4);
  scene.add(ambientLight);

  // Directional light — cool blue-violet tint (not white sun)
  const directionalLight = new THREE.DirectionalLight(0x8899ff, 0.4);
  directionalLight.position.set(40, 40, 40);
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.width = 2048;
  directionalLight.shadow.mapSize.height = 2048;
  directionalLight.shadow.camera.near = 0.5;
  directionalLight.shadow.camera.far = 100;
  directionalLight.shadow.camera.left = -60;
  directionalLight.shadow.camera.right = 60;
  directionalLight.shadow.camera.top = 60;
  directionalLight.shadow.camera.bottom = -60;
  directionalLight.shadow.bias = -0.0001;
  scene.add(directionalLight);

  // Hemisphere light — void above, deep indigo below
  const hemisphereLight = new THREE.HemisphereLight(0x110033, 0x000011, 0.5);
  scene.add(hemisphereLight);
}
